import styles from './Tab.module.scss';
import AvatarWithMarketplace from '@components/AvatarWithMarketplace';
import BeforeReadyTabTable from '../components/BeforeReadyTabTable/BeforeReadyTabTable';
import Box from '@components/Box';
import cn from 'classnames';
import ConditionsModal from '../modals/ConditionsModal';
import DataTable, { DataViewHandle } from '@containers/DataTable';
import DocumentLink from '@components/DocumentLink';
import infoIcon from '@assets/icons/info_blue_16.svg';
import ParcelModalWindow from '@containers/ParcelModalWindow';
import PaymentCompletedModalWindow from '@components/modals/PaymentCompletedModalWindow';
import PaymentModalWindow from '@components/modals/PaymentModalWindow';
import PickupPriceModal from '../modals/PickupPriceModal/PickupPriceModal';
import PickUpStatus from '../components/PickUpStatus/PickUpStatus';
import PlacingOrderModalWindow from '@components/modals/PlacingOrderModalWindow';
import priceIcon from '@assets/icons/price.svg';
import TableSelectionPanel from '@components/Table/TableSelectionPanel';
import TrackingNumber from "@components/TrackingNumber";
import { actionAddresses } from '@utils/handlerSubmitActions';
import { AddSellerPanel } from '@components/AddSeller';
import { Button, ButtonSize, ButtonType } from '@components/Button';
import {
  checkBalanceReplenishment,
  checkBalanceReplenishmentResponse,
  getPaymentLink,
  getWallet,
  sendTopUpBalance,
  sendTopUpBalanceResponse,
  TGetPaymentLinkResponse
} from '@api/apiClient/finance';
import {
  EPlacingOrder,
  TPlacingOrderData,
  TPlacingOrderDataItem
} from '@components/modals/PlacingOrderModalWindow/types';
import { EPlacingOrderViewParcel, getPlacingOrderView } from '@utils/createPlacingOrderView';
import { FilterModel } from '@hooks/useFiltersSearchParams';
import { Filters, FilterTypes } from '@containers/Filters';
import { formatDate, weightGValue, formatNumber } from '@utils/formatValues';
import { getCurrentLanguage, Langs } from '@utils/lang';
import {
  getPickUpParcels,
  getPickUpInfo,
  PickUpParcel,
  getPickUpCost,
  TPickUpCostResponse,
  sendOneTimePickUp,
  TPickUpOrderParams,
  TPickUpOrderAddress
} from '@api/apiClient/pickUp';
import { Modal } from '@components/Modal';
import { Row, Column } from '@components/Grid';
import { TableColumn, TableColumnType, TableRowType } from '@components/Table/TableTypes.ts';
import { Text, TextColor, TextSize, TextType, TextWeight } from '@components/Text';
import { TPlacingOrderListItem } from '@components/PlacingOrderView/types';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useProfile } from '@contexts/ProfileContext';
import { useState, useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';

interface LinkData {
  fileSize: string;
  url: string;
}

const instruction: Record<Langs, LinkData | null> = {
  [Langs.cn]: {
    fileSize: '1,09 MB',
    url: 'https://storage.yandexcloud.net/unitrade-public/UNI%E5%85%8D%E8%B4%B9%E4%B8%8A%E9%97%A8%E6%8F%BD%E6%94%B6%E6%9C%8D%E5%8A%A1%E2%80%94%E2%80%94%E8%AF%B4%E6%98%8E.pdf'
  },
  [Langs.en]: null,
  [Langs.ru]: null,
  [Langs.tr]: null
};

const ReadyTab = ({
                    isFilterOpen,
                    setIsFilterOpen,
                    isNew,
                    onUpdate,
                    sellersNotFound
                  }: {
  isFilterOpen: boolean,
  setIsFilterOpen: (v: boolean) => void,
  isNew: boolean,
  sellersNotFound: boolean,
  onUpdate: () => void,
}) => {
  const [detailId, setDetailId] = useState('');
  const [expandPlacingOrderViewTabs, setExpandPlacingOrderViewTabs] = useState<boolean>(false);
  const [innerFilters, setInnerFilters] = useState<FilterModel[]>([]);
  const [isConditionsOpen, setConditionsOpen] = useState(false);
  const [isInitialized, setIsInitialized] = useState<boolean>(false);
  const [isOpenParcelWindow, setIsOpenParcelWindow] = useState(false);
  const [isOpenPaymentCompletedWindow, setIsOpenPaymentCompletedWindow] = useState<boolean>(false);
  const [isOpenPaymentWindow, setIsOpenPaymentWindow] = useState<boolean>(false);
  const [isOpenPickUpFormWindow, setIsOpenPickUpFormWindow] = useState<boolean>(false);
  const [isPriceOpen, setPriceOpen] = useState(false);
  const [parcelsAnyAllView, setParcelsAnyAllView] = useState<TPlacingOrderListItem[] | undefined>(undefined);
  const [parcelsAnySelectedView, setParcelsAnySelectedView] = useState<TPlacingOrderListItem[] | undefined>(undefined);
  const [parcelsGoodAllView, setParcelsGoodAllView] = useState<TPlacingOrderListItem[] | undefined>(undefined);
  const [parcelsGoodSelectedView, setParcelsGoodSelectedView] = useState<TPlacingOrderListItem[] | undefined>(undefined);
  const [paymentError, setPaymentError] = useState<string | undefined>(undefined);
  const [paymentInProcessed, setPaymentInProcessed] = useState<boolean>(false);
  const [processingCalledFrom, setProcessingCalledFrom] = useState<string>('');
  const [selectedParcels, setSelectedParcels] = useState<Map<string, PickUpParcel>>(new Map());
  const [selectedPickUpCost, setSelectedPickUpCost] = useState<number>(0);
  const [selectedRowIds, setSelectedRowIds] = useState<string[]>([]);
  const [submitParams, setSubmitParams] = useState<TPickUpOrderParams | undefined>(undefined);

  const dataRef = useRef<DataViewHandle<PickUpParcel>>(null);
  const timeoutCheckBalance = useRef<number | null>(null);
  const timeoutCheckPayment = useRef<number | null>(null);

  const currentLanguage = getCurrentLanguage();
  const queryClient = useQueryClient();
  const { account } = useProfile();
  const { t } = useTranslation();

  const pickUpInfo = useQuery({
    queryKey: ['pickUp'],
    queryFn: () => getPickUpInfo(),
    retry: 1,
    staleTime: Infinity
  });

  const refreshData = () => {
    if (dataRef.current?.refresh) {
      dataRef.current.refresh();
    }
    onUpdate();
    setSelectedRowIds([]);
    setSelectedParcels(new Map());
    queryClient.invalidateQueries(
        { queryKey: ['getWallet'] }
    );
  };

  const { factPrice, weightPrice } = useMemo(() => {
    const tarif = (pickUpInfo?.data?.paidTariffSettings || [])
        .find((v) => (v.countryCode === account?.region));
    return {
      factPrice: tarif?.factPrice || 0,
      weightPrice: tarif?.weightPrice || 0
    };
  }, [pickUpInfo?.data, account?.region]);

  const handleSelectionChange = useCallback((
      selectedIds: string[],
      items: PickUpParcel[]
  ) => {
    setSelectedRowIds(selectedIds);

    const parcels: Map<string, PickUpParcel> = new Map();
    selectedParcels.forEach((parcel, key) => {
      const item = items.find((v) => v.id === key);
      if (!item) parcels.set(key, parcel); // selected items from other pages
    });

    selectedIds.forEach((id) => {
      const item = items.find((v) => (v.id === id));
      if (item) {
        parcels.set(id, item);
      } else {
        // selected items from other pages
        const selectedItem = selectedParcels.get(id);
        if (!selectedItem) {
          console.error(`product with id ${id} not found`);
        } else {
          parcels.set(id, selectedItem);
        }
      }
    });
    setSelectedParcels(parcels);
  }, [selectedParcels]);

  const {
    defaultMinCount,
    defaultMinWeightInGrams
  } = pickUpInfo?.data?.serviceConstraintsInfo || {};
  const constraints = (pickUpInfo?.data?.serviceConstraintsInfo?.constraints || []);

  const totalByService = Object.values(Array.from(selectedParcels, ([_, value]) => value)
      .reduce((acc, v) => {
        acc[v.serviceId] = {
          weight: (acc[v.serviceId]?.weight ?? 0) + weightGValue(v.weight),
          count: (acc[v.serviceId]?.count ?? 0) + 1,
          serviceId: v.serviceId,
          serviceName: v.serviceName
        };
        return acc;
      }, {} as Record<string, { weight: number, count: number, serviceId: string, serviceName: string }>));

  let totalByGroup = Object.values(totalByService.reduce((acc, v) => {
    const groupConstraints = constraints.find((c) => (c.services.find((s) => s.id === v.serviceId)));
    const groupId = groupConstraints?.serviceGroupId ?? '';

    acc[groupId] = {
      weight: (acc[groupId]?.weight ?? 0) + v.weight,
      count: (acc[groupId]?.count ?? 0) + v.count,
      groupId,
      services: groupConstraints?.services ?? [],
      groupName: groupConstraints?.serviceGroupName ?? '',
      useTotal: !groupConstraints,
      isAllowed: groupConstraints?.isAllowed ?? true,
      minCount: (groupConstraints ? groupConstraints.minCount : defaultMinCount) || 0,
      minWeight: (groupConstraints ? groupConstraints.minWeightInGrams : defaultMinWeightInGrams) || 0
    };
    return acc;
  }, {} as Record<string, {
    weight: number,
    count: number,
    groupId: string,
    groupName: string,
    useTotal: boolean,
    isAllowed: boolean,
    minCount: number,
    minWeight: number,
    services: { id: string, name: string }[],
  }>));

  const defaultServicesTotal = totalByGroup.reduce((acc, item) => ({
    weight: item.useTotal ? acc.weight + item.weight : acc.weight,
    count: item.useTotal ? acc.count + item.count : acc.count
  }), { weight: 0, count: 0 });

  totalByGroup = totalByGroup.map((item) => ({
    ...item,
    weight: item.useTotal ? defaultServicesTotal.weight : item.weight,
    count: item.useTotal ? defaultServicesTotal.count : item.count
  }));

  const belowLimit = totalByGroup.filter(v => !(
      (v.minCount && (v.count >= v.minCount))
      || (v.minWeight && (v.weight >= v.minWeight))
      || (!v.minWeight && !v.minCount)
  ));
  const freeGroups = totalByGroup.filter(v => (
      (
          (v.minCount && (v.count >= v.minCount))
          || (v.minWeight && (v.weight >= v.minWeight))
      ) && v.isAllowed
  ));
  const notAllowedGroups = totalByGroup.filter(v => !v.isAllowed);
  const freePickUpAvailable = Boolean(freeGroups.length);

  const paidGroups = [...notAllowedGroups, ...(freePickUpAvailable ? [] : belowLimit)];
  const weight = paidGroups.reduce((acc, item) => acc + item.weight, 0);
  const pickUpCost = paidGroups.length ? (
      Math.round((factPrice * 100) + (weightPrice * weight / 10)) / 100
  ) : 0;

  const { mutate: mutateCheckBalanceReplenishment } = useMutation({
    mutationFn: checkBalanceReplenishment
  });

  const { mutate: mutateCheckBalance } = useMutation({
    mutationFn: getWallet,
    onSuccess: (result) => {
      if (result.balance >= selectedPickUpCost) {
        mutateSendOneTimePickUp(submitParams!);
      } else {
        waitingBalanceReplenished();
      }
    }
  });

  const {
    data: dataSendTopUpBalance,
    error: errorSendTopUpBalance,
    isError: isErrorSendTopUpBalance,
    isLoading: isLoadingSendTopUpBalance,
    mutate: mutateSendTopUpBalance,
    reset: resetSendTopUpBalance
  } = useMutation({
    mutationFn: sendTopUpBalance,
    onSuccess: (result: sendTopUpBalanceResponse) => {
      setIsOpenPaymentWindow(true);
      setIsOpenPickUpFormWindow(false);
      setPaymentTimeout();
      waitingBalanceReplenished(result.outTradeNo);
    }
  });

  const {
    error: errorGetPaymentLink,
    isError: isErrorGetPaymentLink,
    isLoading: isLoadingGetPaymentLink,
    mutate: mutateGetPaymentLink
  } = useMutation({
    mutationFn: getPaymentLink,
    onSuccess: (result: TGetPaymentLinkResponse) => {
      setPaymentInProcessed(true);
      setPaymentTimeout();
      waitingBalanceReplenished();
      window.open(result.paymentLink);
    }
  });

  const {
    error: errorSendOneTimePickUp,
    isError: isErrorSendOneTimePickUp,
    isLoading: isLoadingSendOneTimePickUp,
    mutate: mutateSendOneTimePickUp,
    reset: resetSendOneTimePickUp
  } = useMutation({
    mutationFn: sendOneTimePickUp,
    onSuccess: () => {
      onClosePaymentPickUp();
      onClosePickUpProcessing();
      refreshData();
    }
  });

  const { mutate: mutateGetPickUpCost } = useMutation({
    mutationFn: getPickUpCost,
    onSuccess: (result: TPickUpCostResponse) => {
      const condition: boolean = processingCalledFrom === 'all';
      const areTherePaidParcels: boolean = !!result.paidPickUpParcels.length;
      if (condition) {
        if (areTherePaidParcels) {
          setParcelsAnyAllView(result.freePickUpParcels.length
              ? getPlacingOrderView(result.freePickUpParcels, EPlacingOrderViewParcel.PICKUP_READY_CREATE)
              : undefined);
          setParcelsGoodAllView(getPlacingOrderView(result.paidPickUpParcels, EPlacingOrderViewParcel.PICKUP_READY_CREATE));
        } else {
          setParcelsGoodAllView(getPlacingOrderView(result.freePickUpParcels, EPlacingOrderViewParcel.PICKUP_READY_CREATE));
        }
      } else {
        if (areTherePaidParcels) {
          setParcelsAnySelectedView(result.freePickUpParcels.length
              ? getPlacingOrderView(result.freePickUpParcels, EPlacingOrderViewParcel.PICKUP_READY_CREATE)
              : undefined);
          setParcelsGoodSelectedView(getPlacingOrderView(result.paidPickUpParcels, EPlacingOrderViewParcel.PICKUP_READY_CREATE));
        } else {
          setParcelsGoodSelectedView(getPlacingOrderView(result.freePickUpParcels, EPlacingOrderViewParcel.PICKUP_READY_CREATE));
        }
      }
      setExpandPlacingOrderViewTabs(!areTherePaidParcels);
      setIsOpenPickUpFormWindow(true);
      setSelectedPickUpCost(areTherePaidParcels ? result.pickUpCost.amount : 0);
    }
  });

  const waitingBalanceReplenished = (id?: string): void => {
    timeoutCheckBalance.current = window.setTimeout(() => {
      if (id) {
        mutateCheckBalanceReplenishment(id, {
          onSuccess: (data: checkBalanceReplenishmentResponse) => {
            if (data.isProcess) {
              if (!paymentInProcessed) setPaymentInProcessed(true);
              waitingBalanceReplenished(id);
            } else {
              if (paymentInProcessed) setPaymentInProcessed(false);
              if (data.isSuccess) {
                clearTimeout(timeoutCheckPayment.current as number);
                mutateCheckBalance();
              } else {
                if (data.errorCode) {
                  clearTimeout(timeoutCheckPayment.current as number);
                  setPaymentError(data.errorCode);
                } else {
                  waitingBalanceReplenished(id);
                }
              }
            }
          }
        });
      } else {
        mutateCheckBalance();
      }
    }, 3000);
  };

  const closeConditions = () => setConditionsOpen(false);
  const closePrice = () => setPriceOpen(false);

  const closeParcelWindow = () => {
    setIsOpenParcelWindow(false);
  };

  const handleParcelClick = (parcelId: string) => {
    setIsOpenParcelWindow(true);
    setDetailId(parcelId);
  };

  const setPaymentTimeout = (): void => {
    timeoutCheckPayment.current = window.setTimeout(() => {
      clearTimeout(timeoutCheckBalance.current as number);
      setPaymentInProcessed(false);
    }, 600000);
  };

  const onClosePaymentPickUp = (): void => {
    clearTimeout(timeoutCheckBalance.current as number);
    clearTimeout(timeoutCheckPayment.current as number);
    setIsOpenPaymentWindow(false);
    setPaymentInProcessed(false);
  };

  const onClickPaymentLink = (amount: string) => {
    mutateGetPaymentLink(amount);
  };

  const onClosePickUpProcessing = () => {
    resetSendOneTimePickUp();
    resetSendTopUpBalance();
    setIsOpenPickUpFormWindow(false);
    setPaymentInProcessed(false);
  };

  const handlerSubmit = (forms: TPlacingOrderData): void => {
    const condition: boolean = processingCalledFrom === 'all';
    const addresses: TPickUpOrderAddress[] = forms.map((item: TPlacingOrderDataItem) => ({
      address: item.address,
      ...(item.comment && { comment: item.comment }),
      contactInfo: {
        name: item.fullName,
        phone: item.phone
      },
      shipmentDate: formatDate(item.shipmentDate, 'yyyy-MM-dd')
    }));
    const params: TPickUpOrderParams = {
      ...addresses[0],
      parcelIds: condition
          ? [...(parcelsAnyAllView || []).map((item: TPlacingOrderListItem) => item.id), ...parcelsGoodAllView!.map((item: TPlacingOrderListItem) => item.id)]
          : [...(parcelsAnySelectedView || []).map((item: TPlacingOrderListItem) => item.id), ...parcelsGoodSelectedView!.map((item: TPlacingOrderListItem) => item.id)]
    };
    if (account!.balance >= selectedPickUpCost) {
      mutateSendOneTimePickUp(params);
    } else {
      setSubmitParams(params);
      if (account!.region === 'TR') {
        onClosePickUpProcessing();
        setIsOpenPaymentWindow(true);
      } else {
        mutateSendTopUpBalance({
          amount: Math.round((selectedPickUpCost - account!.balance) * 100) / 100
        });
      }
    }
    actionAddresses(forms);
  };

  const createOrder = (calledFrom: string): void => {
    setProcessingCalledFrom(calledFrom);
    setTimeout(() => {
      if (calledFrom === 'all') {
        mutateGetPickUpCost([]);
      }
      if (calledFrom === 'table') {
        mutateGetPickUpCost(Array.from(selectedParcels.values()).map((item: PickUpParcel) => item.id));
      }
    }, 300);
  };

  const BeforeReadyTabTableComponent = useCallback(() => {
    const foundParcels: PickUpParcel[] = dataRef.current?.data ?? [];
    const trackNumberFilter = innerFilters.find((filter: FilterModel) => filter.name === 'trackOrExternalNumbers');
    let warning = null;
    if (trackNumberFilter && trackNumberFilter.value.length && foundParcels) {
      const wantedParcels: string[] = trackNumberFilter.value[0].split(',');
      const notFoundParcels: string[] = (wantedParcels.length > 1)
        ? wantedParcels.filter((track: string) => (
            track && !foundParcels.some((item: PickUpParcel) => (
              item.trackNumber === track
              || item.externalNumber === track
            ))
          ))
        : wantedParcels.filter((track: string) => (
            track && !foundParcels.some((item: PickUpParcel) => (
              (item.trackNumber || "").search(track) > -1
              || (item.externalNumber || "").search(track) > -1
            ))
          ));
      if (foundParcels.length && notFoundParcels.length) {
        const trackNumbers = {
          notFound: notFoundParcels,
          search: wantedParcels
        };
        warning = (<BeforeReadyTabTable trackNumbers={trackNumbers}/>);
      }
    }

    return (
        <>
          {warning}
          {(foundParcels?.length) ? (
              <Box pb={5}>
                <Button
                    onClick={() => createOrder('all')}
                    size={ButtonSize.S}
                    title={t('pickUp.tabReady.orderAllBtn')}
                />
              </Box>
          ) : null}
        </>
    );
  }, [dataRef.current?.data]);

  const columns: TableColumn<PickUpParcel>[] = useMemo(() => [
    {
      header: t("common.trackingNumber"),
      accessor: (row: TableRowType<PickUpParcel>) => (
        <TrackingNumber
          externalId={row.data?.externalNumber}
          trackNumber={row.data?.trackNumber}
          onClick={() => handleParcelClick(row.data?.id)}
        />
      ),
      fixWidth: true,
      noOverflow: true,
      width: '200px'
    },
    {
      header: t('pickUp.tabReady.service'),
      accessor: (row: TableRowType<PickUpParcel>) => row.data.serviceName,
      width: '200px'
    },
    {
      header: t('pickUp.tabReady.store'),
      accessor: (row: TableRowType<PickUpParcel>) => (
          <AvatarWithMarketplace
              id={row.data.sellerId}
              name={row.data.sellerName}
              marketplace={row.data.marketplaceType}
          />
      ),
      alignment: 'center',
      noOverflow: true
    },
    {
      header: t('pickUp.tabReady.weight'),
      accessor: (row: TableRowType<PickUpParcel>) => (
          <Text
              color={TextColor.PRIMARY}
              size={TextSize.S}
          >
            {row.data.weight ? formatNumber(row.data.weight.value) : '—'}
          </Text>
      )
    },
    {
      header: t('pickUp.tabReady.date'),
      accessor: (row: TableRowType<PickUpParcel>) => formatDate(row.data.createdAt, 'dd.MM.yy HH:mm'),
      alignment: 'right',
      type: TableColumnType.TERTIARY
    }
  ], [currentLanguage]);

  return (
      <div>
        <Box pb={5}>
          <Column phone={12}>
            <Row gap={6}>
              <DocumentLink
                  icon={infoIcon}
                  text={t('awaitingPage.downloadInstruction')}
                  {...(instruction[currentLanguage] || instruction[Langs.cn]) as LinkData}
              />
              <Button
                  type={ButtonType.LINK}
                  onClick={() => setConditionsOpen(true)}
                  icon={infoIcon}
                  title={(
                      <Text
                          weight={TextWeight.MEDIUM}
                          size={TextSize.L}
                      >
                        {t('pickUp.tabReady.conditionsLink')}
                      </Text>
                  )}
              />
              <Button
                  type={ButtonType.LINK}
                  onClick={() => setPriceOpen(true)}
                  icon={priceIcon}
                  title={(
                      <Text
                          weight={TextWeight.MEDIUM}
                          size={TextSize.L}
                      >
                        {t('pickUp.tabReady.priceLink')}
                      </Text>
                  )}
              />
            </Row>
          </Column>
        </Box>
        {!sellersNotFound && (
            <Box pb={5}>
              <Column phone={12}>
                <Row>
                  <Column phone={12}>
                    <Text
                        size={TextSize.S}
                        type={TextType.BLOCK}
                    >
                      {t('pickUp.tabReady.description')}
                    </Text>
                  </Column>
                </Row>
              </Column>
            </Box>
        )}
        <Box
            pb={5}
            className={cn(!isFilterOpen && styles.hidden)}
        >
          <Column phone={12}>
            <Row
                justify="right"
                gap={5}
            >
              <Filters
                  id="pickUp-ready"
                  openFilters={() => setIsFilterOpen(true)}
                  items={[
                    {
                      name: 'trackOrExternalNumbers',
                      type: FilterTypes.MULTI_SEARCH,
                      placeholder: t('filters.searchByTrackNumber')
                    },
                    {
                      name: 'serviceIds',
                      type: FilterTypes.SERVICE_PICKUP
                    },
                    {
                      name: 'sellerIds',
                      type: FilterTypes.SELLER
                    },
                    {
                      name: 'marketplaceType',
                      type: FilterTypes.MARKETPLACE
                    },
                    {
                      name: 'dateRange',
                      type: FilterTypes.DATE_RANGE,
                      placeholder: t('pickUp.tabReady.date'),
                      fieldNames: ['startDate', 'endDate']
                    }
                  ]}
                  onChange={(v, isReady) => {
                    if (isReady) {
                      setIsInitialized(true);
                    }
                    setInnerFilters(v);
                  }}
                  values={innerFilters}
                  asPanel
              />
            </Row>
          </Column>
        </Box>
        {sellersNotFound ? (
            <AddSellerPanel
                onSuccess={onUpdate}
                isFirstSeller
            />
        ) : (
            <TableSelectionPanel
                isShown={selectedRowIds.length > 0}
                items={[
                  {
                    label: t('pickUp.tabReady.selected'),
                    value: (
                        <Text
                            size={TextSize.L}
                            color={freePickUpAvailable ? TextColor.WHITE : TextColor.ERROR_DARK}
                            weight={TextWeight.BOLD}
                        >
                          {selectedRowIds.length}
                        </Text>
                    )
                  },
                  {
                    label: t('pickUp.tabReady.pickUpCost'),
                    value: !pickUpCost ? (
                        <Text
                            size={TextSize.L}
                            color={TextColor.SUCCESS_DARK}
                            weight={TextWeight.BOLD}
                        >
                          {t('pickUp.tabReady.pickUpCostFree')}
                        </Text>
                    ) : (
                        <Text
                            size={TextSize.L}
                            color={TextColor.ERROR_DARK}
                            weight={TextWeight.BOLD}
                        >
                          {pickUpCost}
                        </Text>
                    )
                  }
                ]}
                buttons={[
                  (<PickUpStatus
                      belowLimit={belowLimit}
                      notAllowedGroups={notAllowedGroups}
                      freeGroups={freeGroups}
                  />),
                  (<Button
                      key="order"
                      onClick={() => createOrder('table')}
                      size={ButtonSize.S}
                      title={t('pickUp.tabReady.orderBtn')}
                  />)
                ]}
            >
              <DataTable<PickUpParcel>
                  Before={BeforeReadyTabTableComponent}
                  queryKey="pickUpParcels"
                  pending={!isInitialized || !pickUpInfo.isFetched}
                  getData={getPickUpParcels}
                  columns={columns}
                  selectable
                  selectedIds={selectedRowIds}
                  onSelectionChange={handleSelectionChange}
                  notFound={t('pickUp.tabReady.noParcels')}
                  filters={innerFilters}
                  defaultPageSize={20}
                  dataRef={dataRef}
              />
            </TableSelectionPanel>
        )}
        <Modal
            isOpen={isOpenParcelWindow}
            onClose={closeParcelWindow}
            onClickOverlay={closeParcelWindow}
            width={900}
            bodyWithoutIndentations
        >
          <ParcelModalWindow
              parcelId={detailId}
          />
        </Modal>
        <Modal
            isOpen={isOpenPickUpFormWindow}
            onClickOverlay={onClosePickUpProcessing}
            onClose={onClosePickUpProcessing}
            width={650}
        >
          <PlacingOrderModalWindow
              costOfService={selectedPickUpCost}
              errorSendPlacingOrder={errorSendOneTimePickUp}
              errorSendTopUpBalance={errorSendTopUpBalance}
              expandTabs={expandPlacingOrderViewTabs}
              handlerOnClosePlacingOrderProcessing={onClosePickUpProcessing}
              handlerResetSendOrder={resetSendOneTimePickUp}
              handlerSubmit={handlerSubmit}
              isErrorSendPlacingOrder={isErrorSendOneTimePickUp}
              isErrorSendTopUpBalance={isErrorSendTopUpBalance}
              isLoadingSendPlacingOrder={isLoadingSendOneTimePickUp}
              isLoadingSendTopUpBalance={isLoadingSendTopUpBalance}
              isNew={isNew!}
              parcelsAny={processingCalledFrom === 'all' ? parcelsAnyAllView : parcelsAnySelectedView}
              parcelsGood={processingCalledFrom === 'all' ? parcelsGoodAllView : parcelsGoodSelectedView}
              placingOrderInProcessed={isLoadingSendOneTimePickUp}
              placingOrderType={EPlacingOrder.PICKUP_READY}
          />
        </Modal>
        <Modal
            isOpen={isOpenPaymentWindow}
            onClickOverlay={onClosePaymentPickUp}
            onClose={onClosePaymentPickUp}
            width={560}
        >
          <PaymentModalWindow
              dataSendTopUpBalance={dataSendTopUpBalance!}
              errorGetPaymentLink={errorGetPaymentLink}
              handlerOnClickPaymentLink={onClickPaymentLink}
              isErrorGetPaymentLink={isErrorGetPaymentLink}
              isLoadingGetPaymentLink={isLoadingGetPaymentLink}
              isLoadingSendTopUpBalance={isLoadingSendTopUpBalance}
              mutateSendTopUpBalance={mutateSendTopUpBalance}
              paymentError={paymentError}
              paymentInProcessed={paymentInProcessed}
              selectedSum={selectedPickUpCost}
          />
        </Modal>
        <Modal
            isOpen={isOpenPaymentCompletedWindow}
            onClickOverlay={() => setIsOpenPaymentCompletedWindow(false)}
            onClose={() => setIsOpenPaymentCompletedWindow(false)}
            width={560}
        >
          <PaymentCompletedModalWindow
              handlerSetIsOpenPaymentCompletedWindow={setIsOpenPaymentCompletedWindow}
              subTitle={t('pickupOrder.successDescription')}
              title={t('pickupOrder.successTitle')}
          />
        </Modal>
        <Modal
            isOpen={isConditionsOpen}
            onClose={closeConditions}
            onClickOverlay={closeConditions}
            width={980}
        >
          <ConditionsModal/>
        </Modal>
        <Modal
            isOpen={isPriceOpen}
            onClose={closePrice}
            onClickOverlay={closePrice}
            width={618}
        >
          <PickupPriceModal
              factPrice={factPrice}
              weightPrice={weightPrice}
          />
        </Modal>
      </div>
  );
};

export default ReadyTab;
